import { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const faqs = [
    { q: 'How can I assess the knowledge of a user when I send them to a web page or YouTube video?', a: 'You can set any number of assessment questions for learners to answer while they consume the content or afterwards. For now, these questions are choice questions only, but we will be adding more question types very soon.' },
    { q: 'Can I upload my own content such as SCORM, PowerPoints, or PDFs?', a: 'Yes. You can upload SCORM packages, PowerPoints, or PDFs. We convert the latter two to HTML5 and present them to the user after they have consumed the core material.' },
    { q: 'Can I send learners to more than one page?', a: 'Not yet, but soon. For now badge-earning opportunities are single-page, but we will shortly allow you to define any number of pages on any number of sites.' },
    { q: 'Can I stack badges?', a: 'We follow the Open Badges 2.0+ standard, so you will be able to define stacking badges. This means you can build both simple ad hoc awards and deep learning pathways.' },
    { q: 'Can I direct people to just my badges rather than the main library?', a: 'Yes. Each issuer gets their own page that shows only their badges, along with your issuer logo and description.' },
    { q: 'What is a Private Badge?', a: 'When you create a badge you have extensive control over who can earn it. Private badges can be limited to users from your organization or specific group, protected by an access code.' },
    { q: 'How many badges can I issue?', a: 'There is no limit on the number of badges you can issue on the free plan.' },
    { q: 'What is the cost to use TC Everywhere?', a: 'TC Everywhere is free for individuals, educators, and non-profits — always. Premium organizational features are available for larger teams that need advanced capabilities.' },
    { q: 'Who owns the badges and badge data?', a: 'You do. TC Everywhere acts as a platform for issuing and storing badges, but the credentials and the underlying data belong to the issuer and the earner.' },
    { q: 'What if I want to move off TC Everywhere? Or what if TC Everywhere disappears?', a: 'You can export any or all of your badges at any time and add them to any other Open Badges-compatible platform. We will continue to store the data about how the badge was earned even after you export.' },
    { q: 'What is the API for?', a: "The API lets your internal systems create and issue badges programmatically, and bring badging data back into the HR or LMS systems that need it." },
    { q: "I've issued some badges, but now I can't delete my badge. What can I do?", a: "Once you have issued a badge, you cannot delete it — the credential exists in earners' portfolios. If you want to stop offering the badge, set it to Inactive." },
];

export default function FaqsPage() {
    const [expanded, setExpanded] = useState<number | false>(0);

    return (
        <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 10, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" fontWeight={700} gutterBottom>Frequently Asked Questions</Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>Everything you need to know about TC Everywhere.</Typography>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="md">
                    {faqs.map((faq, i) => (
                        <Accordion
                            key={i}
                            expanded={expanded === i}
                            onChange={() => setExpanded(expanded === i ? false : i)}
                            elevation={0}
                            sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: '12px !important', '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>{faq.q}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">{faq.a}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8, bgcolor: 'background.paper', textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h5" fontWeight={700} gutterBottom>Still have questions?</Typography>
                    <Typography color="text.secondary" mb={3}>We're happy to help. Reach out and we'll get back to you quickly.</Typography>
                    <Button component={Link} to="/contact" variant="contained" size="large">Contact Us</Button>
                </Container>
            </Box>
        </>
    );
}
